import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
} from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { stringify } from 'qs';

import {
  SuspendedUseQueryOptions,
  useSuspendedQuery,
  useZepApiClient,
  UseZepApiMutationOptions,
  UseZepApiQueryOptions,
} from '@zep/apis';
import { SpaceSimpleDto } from '@zep/types';

import {
  PremiumSpaceKeys,
  SpaceQueryKeys,
  SpaceRole,
  SpaceUserRole,
  VisitedSpaceKeys,
} from '../consts';
import { CursorPaginator, Space } from '../types/SpaceTypes';
import {
  SpacePlanDto,
  SpaceWithPlanListResponse,
} from '../types/SubscriptionType';

type RoleSpace = Omit<SpaceSimpleDto, 'spacePlanType'> & SpacePlanDto;

const makeMyRoleSpacesUri = (
  _roles: SpaceRole[],
  next?: string,
  limit = 24,
) => {
  const query = stringify(
    {
      next,
      limit,
      roles: _roles.map(role => SpaceUserRole[role]),
    },
    {
      arrayFormat: 'repeat',
    },
  );

  return `/v2/spaces/filter-role-with-plan?${query}`;
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useMyRoleSpaces = (
  _roles: SpaceRole[],
  next?: string,
  options?: UseZepApiQueryOptions<{
    status: number;
    spaces: RoleSpace[];
    cursorPaginator: CursorPaginator;
  }>,
) => {
  const client = useZepApiClient();
  const uri = makeMyRoleSpacesUri(_roles, next);

  return useQuery(
    VisitedSpaceKeys.list('upgrade_plans'),
    async () => {
      const { data } = await client.get(uri);
      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useInfiniteMyRoleSpaces = (_roles: SpaceRole[]) => {
  const client = useZepApiClient();
  return useInfiniteQuery(
    VisitedSpaceKeys.list('above_admin'),
    async ({ pageParam = '' }: QueryFunctionContext) => {
      const uri = makeMyRoleSpacesUri(_roles, pageParam, 24);
      const { data } = await client.get(uri);
      return data;
    },
    {
      getNextPageParam: ({ cursorPaginator }) =>
        cursorPaginator.hasNext ? cursorPaginator.next : undefined,
      keepPreviousData: false,
      suspense: true,
    },
  );
};

/**
 * @alias {API_SPACE_PLANS}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useActiveSpacePlanMutation = (
  options?: UseZepApiMutationOptions<
    {
      status: number;
      statusKey: string;
      data: {
        activeSpacePlan: SpacePlanDto;
      };
    },
    { spaceHashId: string }
  >,
) => {
  const client = useZepApiClient();

  return useMutation(
    [PremiumSpaceKeys.SPACE_PLAN],
    async ({ spaceHashId }) => {
      const { data } = await client.get('/v2/spaces/plans/active', {
        params: {
          spaceHashId,
        },
      });

      return data;
    },
    options,
  );
};

/**
 * @alias {API_SPACE_PLANS}
 */
/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useActiveSpacePlanQuery = (
  spaceHashId: string,
  options?: UseZepApiQueryOptions<
    {
      status: number;
      statusKey: string;
      data: {
        activeSpacePlan: SpacePlanDto;
      };
    },
    {
      activeSpacePlan: SpacePlanDto;
    }
  >,
) => {
  const client = useZepApiClient();

  return useQuery(
    [PremiumSpaceKeys.SPACE_PLAN],
    async () => {
      const { data: spacePlan } = await client.get('/v2/spaces/plans/active', {
        params: {
          spaceHashId,
        },
      });

      return spacePlan.data;
    },

    // @ts-ignore
    options,
  );
};

const fetchSpacePlans = (client: AxiosInstance) => {
  return client.get('/v2/me/space-plans', {
    timeout: 10000,
  });
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSubscribedSpaces = (
  userHashId?: string,
  options?: UseZepApiQueryOptions<SpaceWithPlanListResponse>,
) => {
  const client = useZepApiClient();

  return useQuery(
    [PremiumSpaceKeys.channelTalkSubscribed, userHashId ?? ''],
    async () => {
      const { data } = await fetchSpacePlans(client);
      return data;
    },

    // @ts-ignore
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useSuspendedSubscribedSpaces = (
  userHashId?: string,
  options?: SuspendedUseQueryOptions<SpaceWithPlanListResponse, AxiosError>,
) => {
  const client = useZepApiClient();

  return useSuspendedQuery(
    [PremiumSpaceKeys.subscribed, userHashId ?? ''],
    async () => {
      const { data } = await fetchSpacePlans(client);
      return data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useChangeSpaceOwnerMutation = (
  options?: UseMutationOptions<
    {
      targetPlayer: { id: number };
      space: Space;
    },
    AxiosError,
    { targetPlayerEmail?: string; spaceUri?: string }
  >,
) => {
  const client = useZepApiClient();
  return useMutation(
    [SpaceQueryKeys.CHANGE_OWNER],
    async params => {
      const { data } = await client.post('/temp/spaces/change-owner', params);
      return data;
    },
    options,
  );
};
